import React, { useState } from 'react';
import type { Agent } from '../../lib/agent-types';

interface Props {
  agent: Agent;
  onClose: () => void;
}

export function AgentTester({ agent, onClose }: Props) {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState<{ text: string; timestamp: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  const handleTest = async () => {
    if (!input.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/agents/${agent.id}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input }),
      });
      const data = await res.json();
      setOutput({ text: data.output || data.error || 'No response', timestamp: data.timestamp });
    } catch (err) {
      setOutput({ text: 'Request failed: ' + (err as Error).message, timestamp: new Date().toISOString() });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Test: {agent.name}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>
        
        <div className="mb-4 p-3 bg-gray-50 rounded text-sm">
          <div><strong>Model:</strong> {agent.model}</div>
          <div><strong>Tools:</strong> {agent.tools.join(', ') || 'none'}</div>
          <div className="mt-2 p-2 bg-white rounded border">
            <strong>Instructions:</strong>
            <p className="mt-1 text-gray-700 whitespace-pre-wrap">{agent.instructions}</p>
          </div>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Input</label>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
            placeholder="Enter test input..."
          />
          <button
            onClick={handleTest}
            disabled={loading || !input.trim()}
            className="mt-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
          >
            {loading ? 'Running...' : 'Run Agent'}
          </button>
        </div>
        
        {output && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Output</label>
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md font-mono text-sm whitespace-pre-wrap">
              {output.text}
            </div>
            <p className="text-xs text-gray-400 mt-1">at {new Date(output.timestamp).toLocaleTimeString()}</p>
          </div>
        )}
      </div>
    </div>
  );
}
